import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatCurrency } from '@/lib/api';
import { useAuth, isAreaManager, isInstaller } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Package, FileSignature, Calendar, FilePlus2, TrendingUp, ArrowRight, Mail, CheckCircle2, XCircle, Users2, Wallet, MessageSquare, Trophy } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';

const STATUS_COLORS = { bozza: '#94a3b8', compilato: '#0ea5a4', firmato: '#0F766E', inviato: '#84CC16', annullato: '#ef4444' };

export default function Dashboard() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  // Installatori: redirect immediato alla loro area dedicata
  useEffect(() => {
    if (isInstaller(me)) navigate('/installer/interventi', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);
  const isAM = isAreaManager(me);
  const [stats, setStats] = useState(null);
  const [amStats, setAmStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, comp] = await Promise.all([api.getStats(), api.listContracts(), api.listCompanies()]);
        setStats(s);
        setRecent(c.slice(0, 6));
        setCompanies(comp);
      } catch (e) {
        console.error(e);
      }
      try {
        const logs = await api.listEmailLogs({ limit: 8 });
        setEmailLogs(logs);
      } catch (e) {
        console.error(e);
      }
      if (isAM) {
        try {
          const am = await api.getAreaManagerStats();
          setAmStats(am);
        } catch (e) {
          console.error(e);
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAM]);

  const tiles = [
    { label: 'Contratti totali', value: stats?.total_contracts ?? '—', icon: FileSignature, accent: 'text-primary' },
    { label: 'Contratti firmati', value: stats?.signed_contracts ?? '—', icon: TrendingUp, accent: 'text-emerald-600' },
    { label: 'Aziende partner', value: stats?.total_partners ?? '—', icon: Building2, accent: 'text-sky-600' },
    { label: 'Prodotti a catalogo', value: stats?.total_products ?? '—', icon: Package, accent: 'text-amber-600' },
  ];

  const monthly = (stats?.by_month || []).slice(-6).map(m => ({ month: m.month?.slice(2) || '', count: m.count }));
  const byStatus = (stats?.by_status || []).map(s => ({ name: s.status, value: s.count }));

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Panoramica generale del generatore di contratti per efficientamento energetico"
        actions={
          <Button asChild data-testid="dashboard-new-contract"><Link to="/contracts/new"><FilePlus2 className="size-4 mr-2" />Nuovo contratto</Link></Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {tiles.map(t => {
          const Icon = t.icon;
          return (
            <Card key={t.label} className="rounded-xl" data-testid={`kpi-${t.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.label}</div>
                    <div className="text-3xl font-semibold tabular-nums mt-2">{t.value}</div>
                  </div>
                  <div className={`p-2 rounded-lg bg-secondary/70 ${t.accent}`}><Icon className="size-5" /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isAM && amStats && (
        <div className="mb-6 space-y-4" data-testid="area-manager-dashboard">
          <div className="flex items-center gap-2">
            <Trophy className="size-5 text-primary" />
            <h2 className="text-lg font-semibold">Il tuo team ({amStats.team_size} agent{amStats.team_size === 1 ? 'e' : 'i'})</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="rounded-xl border-primary/30 bg-primary/5" data-testid="am-kpi-signed-month">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Firmati (mese)</div>
                <div className="text-2xl font-semibold tabular-nums mt-1">{amStats.contracts_month}</div>
                <div className="text-xs text-muted-foreground mt-1">Totali firmati: {amStats.signed_contracts}</div>
              </CardContent>
            </Card>
            <Card className="rounded-xl" data-testid="am-kpi-quotes-month">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Preventivi (mese)</div>
                <div className="text-2xl font-semibold tabular-nums mt-1">{amStats.quotes_month}</div>
                <div className="text-xs text-muted-foreground mt-1">Totale contratti: {amStats.total_contracts}</div>
              </CardContent>
            </Card>
            <Card className="rounded-xl" data-testid="am-kpi-revenue">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Wallet className="size-3" />Fatturato firmato</div>
                <div className="text-2xl font-semibold tabular-nums mt-1">{formatCurrency(amStats.total_revenue || 0)}</div>
              </CardContent>
            </Card>
            <Card className="rounded-xl" data-testid="am-kpi-open-tickets">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MessageSquare className="size-3" />Ticket aperti</div>
                <div className="text-2xl font-semibold tabular-nums mt-1">{amStats.open_tickets}</div>
                <div className="text-xs text-muted-foreground mt-1">Dagli agenti del team</div>
              </CardContent>
            </Card>
            <Card className="rounded-xl" data-testid="am-kpi-team-size">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Users2 className="size-3" />Team</div>
                <div className="text-2xl font-semibold tabular-nums mt-1">{amStats.team_size}</div>
                <Button asChild size="sm" variant="ghost" className="mt-1 -ml-2 h-7 text-xs">
                  <Link to="/users">Gestisci <ArrowRight className="size-3 ml-1" /></Link>
                </Button>
              </CardContent>
            </Card>
          </div>
          {amStats.top_agents && amStats.top_agents.length > 0 && (
            <Card data-testid="am-top-agents">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy className="size-4 text-amber-500" />Top 3 agenti per fatturato firmato</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {amStats.top_agents.map((a, i) => (
                    <div key={a.agent_id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`am-top-agent-${i}`}>
                      <div className="flex items-center gap-3">
                        <Badge variant={i === 0 ? 'default' : 'secondary'}>#{i + 1}</Badge>
                        <div>
                          <div className="font-medium">{a.name}</div>
                          <div className="text-xs text-muted-foreground">{a.signed} contratt{a.signed === 1 ? 'o' : 'i'} firmat{a.signed === 1 ? 'o' : 'i'}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold tabular-nums text-primary">{formatCurrency(a.revenue)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {amStats.team_size === 0 && (
            <Card className="border-dashed" data-testid="am-empty-team">
              <CardContent className="p-6 text-center">
                <Users2 className="size-8 text-muted-foreground mx-auto mb-2" />
                <div className="font-medium">Nessun agente assegnato al tuo team</div>
                <p className="text-sm text-muted-foreground mt-1">Chiedi al tuo B.O. di assegnarti degli agenti dalla pagina Utenti (campo &quot;Area Manager di riferimento&quot;).</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Contratti per mese</CardTitle></CardHeader>
          <CardContent className="h-[260px]">
            {monthly.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">Nessun dato ancora disponibile</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Ripartizione stati</CardTitle></CardHeader>
          <CardContent className="h-[260px]">
            {byStatus.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">Nessun contratto</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                    {byStatus.map((e, i) => (<Cell key={i} fill={STATUS_COLORS[e.name] || '#94a3b8'} />))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Attività recente</CardTitle>
          <Button asChild variant="ghost" size="sm"><Link to="/contracts">Vedi tutti <ArrowRight className="size-4 ml-1" /></Link></Button>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {companies.length === 0 ? 'Inizia creando una azienda partner.' : 'Nessun contratto creato ancora.'}
              <div className="mt-4">
                <Button asChild size="sm"><Link to={companies.length === 0 ? '/partners' : '/contracts/new'}>{companies.length === 0 ? 'Crea partner' : 'Nuovo contratto'}</Link></Button>
              </div>
            </div>
          ) : (
            <div className="divide-y">
              {recent.map(c => (
                <Link key={c.id} to={`/contracts/${c.id}`} className="flex items-center justify-between py-3 hover:bg-secondary/40 px-2 rounded-md">
                  <div className="min-w-0">
                    <div className="text-sm font-medium font-mono truncate">{c.contract?.number || '—'}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.customer?.first_name} {c.customer?.last_name}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="capitalize">{c.status}</Badge>
                    <span className="text-sm font-medium tabular-nums hidden sm:inline">{formatCurrency(c.totals?.total)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="dashboard-email-logs">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Mail className="size-4" />Ultimi invii email</CardTitle>
        </CardHeader>
        <CardContent>
          {emailLogs.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nessuna email inviata ancora.
              <div className="text-xs mt-1">Invia un preventivo o un contratto per vedere lo storico qui.</div>
            </div>
          ) : (
            <div className="divide-y">
              {emailLogs.map(l => (
                <div key={l.id} className="py-2.5 px-1" data-testid={`email-log-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {l.status === 'sent'
                        ? <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        : <XCircle className="size-3.5 text-destructive shrink-0" />}
                      <span className="text-sm font-medium truncate">{l.to_email}</span>
                    </div>
                    <Badge variant="outline" className="capitalize text-[10px] shrink-0">{l.type}</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5 pl-5.5">
                    <span className="text-xs text-muted-foreground truncate pl-5">
                      {l.document_number ? `${l.document_number} · ` : ''}{l.company_name}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                      {l.created_at ? new Date(l.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  {l.status === 'failed' && l.error && (
                    <div className="text-[11px] text-destructive mt-0.5 pl-5 truncate">{l.error}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
